import { Drawer, DrawerBody, DrawerHeader, DrawerHeading, SubmitButton, TextField } from "@dgs/core";
import { Form, Formik } from "formik";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";

export interface PasswordFormValues {
	oldPassword: string;
	newPassword: string;
	newPasswordConfirmation: string;
}

const initialValues: PasswordFormValues = {
	oldPassword: "",
	newPassword: "",
	newPasswordConfirmation: "",
};

interface Props {
	open: boolean;
	onClose: () => void;
	onChangePassword: (formValues: PasswordFormValues) => Promise<void>;
}

export const ChangePasswordDrawer: FC<Props> = ({ open, onClose, onChangePassword }) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: PasswordFormValues) => {
			const errors: { [K in keyof PasswordFormValues]?: string } = {};

			if (!values.oldPassword) {
				errors.oldPassword = t("Field is required");
			}

			if (!values.newPassword) {
				errors.newPassword = t("Field is required");
			} else if (values.newPassword.length < 12) {
				errors.newPassword = t("The password needs to be at least 12 characters long.");
			}

			if (!values.newPasswordConfirmation) {
				errors.newPasswordConfirmation = t("Field is required");
			}
			if (
				values.newPassword &&
				values.newPasswordConfirmation &&
				values.newPassword !== values.newPasswordConfirmation
			) {
				errors.newPasswordConfirmation = t("Passwords do not match");
			}

			return errors;
		},
		[t],
	);

	const handleSubmit = useCallback(
		async (formValues: PasswordFormValues) => {
			await onChangePassword(formValues);
			onClose();
		},
		[onChangePassword, onClose],
	);

	return (
		<Drawer open={open} onClose={onClose}>
			<DrawerHeader onClose={onClose}>
				<DrawerHeading>{t("Change password")}</DrawerHeading>
			</DrawerHeader>
			<DrawerBody>
				<Formik onSubmit={handleSubmit} initialValues={initialValues} validate={validate}>
					<Form>
						<TextField type="password" label={t("Old password")} name="oldPassword" />
						<TextField type="password" label={t("New password")} name="newPassword" />
						<TextField type="password" label={t("Repeat password")} name="newPasswordConfirmation" />
						<SubmitButton>{t("Submit")}</SubmitButton>
					</Form>
				</Formik>
			</DrawerBody>
		</Drawer>
	);
};
